// ================== Farb-Utilities ================== //

import { DataModel, Visual } from "./AppTypes";
// ------------------ Theme-Variablen ------------------ //

/**
 * Erzeugt alle CSS-Custom-Properties für Light/Dark Mode
 * auf Basis eines zentralen User-Hue.
 */
export function makeThemeVars(hue: number) {
  const contrastHue = (hue + 120) % 360;

  return {
    "--hue": `${hue}`,
    "--hue-contrast": `${contrastHue}`,

    // Heading-Farbe
    "--heading-color-light": `oklch(0.45 0.12 ${hue})`,
    "--heading-color-dark": `oklch(0.70 0.12 ${hue})`,

    // Selected (kräftig und dominant)
    "--selected-bg-light": `oklch(0.65 0.20 ${contrastHue})`,
    "--selected-bg-dark": `oklch(0.60 0.20 ${contrastHue})`,

    // Hover (mittlere Betonung)
    "--hover-bg-light": `oklch(0.80 0.08 ${contrastHue})`,
    "--hover-bg-dark": `oklch(0.50 0.08 ${contrastHue})`,

    // Associated (leichter Akzent)
    "--associated-bg-light": `oklch(0.95 0.03 ${contrastHue})`,
    "--associated-bg-dark": `oklch(0.40 0.03 ${contrastHue})`,

    // Lesson-Box Standard-Hintergrund
    "--lesson-bg-light": `oklch(0.95 0.03 ${hue})`,
    "--lesson-bg-dark": `oklch(0.40 0.03 ${hue})`,

    // Multi-Count Zahl
    "--multi-count-color-light": `oklch(0.55 0.05 ${hue})`,
    "--multi-count-color-dark": `oklch(0.75 0.05 ${hue})`,

    // Quality-Display Farben – an Hue gekoppelt
    "--quality-bg": `color-mix(in oklch, oklch(0.94 0.06 ${hue}) 60%, transparent)`,
    "--quality-border": `oklch(0.70 0.14 ${hue})`,
    "--quality-text": `oklch(0.40 0.12 ${hue})`,
  };
}

// ------------------ Light/Dark Mode ------------------ //

export type Mode = "light" | "dark";

/** Liefert den aktuellen Browsermodus ("light" | "dark") */
export function getSystemMode(): Mode {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

// ------------------ Basisfarben ------------------ //

/**
 * Berechnet eine Grundfarbe (OKLCH) für UI-Elemente,
 * die nicht auf den Lesson-Background angewiesen sind.
 */
export function getBaseColor(
  level: 1 | 2 | 3 | 4 | 5,
  type: string,
  baseHue: number,
  mode: Mode = "light",
  override?: Visual
): string {
  const safeMode: Mode = mode === "dark" ? "dark" : "light";

  // Lightness & Chroma je Ebene
  const baseLightness = {
    light: [0.99, 0.96, 0.9, 0.9, 0.87],
    dark: [0.5, 0.44, 0.4, 0.36, 0.32],
  }[safeMode][level - 1];

  const baseChroma = {
    light: [0.02, 0.025, 0.03, 0.035, 0.04],
    dark: [0.05, 0.06, 0.07, 0.08, 0.09],
  }[safeMode][level - 1];

  // Hue-Offsets für Typen
  const hueOffsets: Record<string, number> = {
    section: 0,
    weekday: 60,
    phase: 75,
    person: 90,
    team: 105,
    place: 120,
    theme: 135,
    connection: 165,
    disconnection: 195,
    count: 225,
    distinct: 245,
    block: 265,
    align: 285,
    follow: 295,
  };

  let hue = (baseHue + (hueOffsets[type] || 0)) % 360;
  let lightness = baseLightness;
  let chroma = baseChroma;

  if (override) {
    ({
      l: lightness,
      c: chroma,
      h: hue,
    } = applyVisualToComponents(
      { l: lightness, c: chroma, h: hue },
      override,
      safeMode
    ));
  }

  return formatOKLCH({ l: lightness, c: chroma, h: hue });
}

// ------------------ Parser & Formatter ------------------ //

/** Parsed oklch() → Komponenten */
function parseOKLCH(str: string): { l: number; c: number; h: number } | null {
  const m = str.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/i);
  if (!m) return null;
  return { l: parseFloat(m[1]), c: parseFloat(m[2]), h: parseFloat(m[3]) };
}

/** Formatiert Komponenten → oklch() */
function formatOKLCH({ l, c, h }: { l: number; c: number; h: number }): string {
  return `oklch(${clamp01(l).toFixed(3)} ${Math.max(0, c).toFixed(3)} ${wrapHue(
    h
  )})`;
}

function clamp01(x: number) {
  return Math.max(0, Math.min(1, x));
}
function wrapHue(h: number) {
  return ((h % 360) + 360) % 360;
}

// ------------------ Visuals ------------------ //

/** Wendet eine einzelne Visual-Regel auf L/C/H an */
function applyVisualToComponents(
  { l, c, h }: { l: number; c: number; h: number },
  visual: Visual,
  mode: Mode
): { l: number; c: number; h: number } {
  const val = visual.value ?? 0;
  switch (visual.type) {
    case "lightness":
      l = mode === "dark" ? l + val / 100 : l - val / 100;
      break;
    case "chroma":
      c = c * (1 + (val * 10) / 100);
      break;
    case "hue":
      h = wrapHue(val);
      break;
  }
  return { l, c, h };
}

/** Wendet eine einzelne Visual-Regel auf eine OKLCH-Farbe an */
export function applySingleVisual(
  base: string,
  visual: Visual,
  mode: Mode
): string {
  if (!visual || visual.type === "default") return base;
  const parsed = parseOKLCH(base);
  if (!parsed) return base;
  return formatOKLCH(applyVisualToComponents(parsed, visual, mode));
}

/** Wendet mehrere Visuals nacheinander auf Basisfarbe an */
export function applyVisuals(
  visuals: Visual[],
  mode: Mode,
  cssVar = "--lesson-bg",
  fallback = "oklch(0.7 0.05 200)"
): string {
  let base = getCssVar(cssVar, fallback);
  const parsed = parseOKLCH(base);
  if (!parsed) return base;

  let { l, c, h } = parsed;
  for (const v of visuals) {
    if (v && v.type !== "default") {
      ({ l, c, h } = applyVisualToComponents({ l, c, h }, v, mode));
    }
  }
  return formatOKLCH({ l, c, h });
}

/**
 * Liefert die Hintergrundfarbe einer Lesson-Zelle.
 * Sammelt alle Visuals aus den relevanten Dimensionen.
 */
export function getLessonBg(
  data: DataModel,
  row: Record<string, string>
): string {
  const mode = getSystemMode();
  const visuals: Visual[] = [];
  data.dimensions.forEach((dim) => {
    const pointName = row[dim.name];
    const point = dim.points.find((p) => p.name === pointName);
    if (point) {
      visuals.push(point.visual);
      point.tagRefs.forEach((tagId) => {
        const tag = dim.tags.find((t) => t.id === tagId);
        if (tag) visuals.push(tag.visual);
      });
    }
  });
  return applyVisuals(visuals, mode, "--lesson-bg");
}

// ------------------ CSS-Helper ------------------ //

/** Holt eine CSS-Variable vom :root, sonst Fallback */
export function getCssVar(name: string, fallback: string): string {
  const val = getComputedStyle(document.documentElement).getPropertyValue(name);
  return val?.trim() || fallback;
}

// ------------------ Fulfillment Palette ------------------ //

export type FulfillmentTone = "excellent" | "good" | "fair" | "poor";

type PaletteEntry = {
  bgVar: string;
  textVar: string;
  fallbackBg: string;
  fallbackText: string;
};

const FULFILLMENT_PALETTE: Record<
  FulfillmentTone,
  { light: PaletteEntry; dark: PaletteEntry }
> = {
  excellent: {
    light: {
      bgVar: "--fulfillment-excellent-bg-light",
      textVar: "--fulfillment-excellent-text-light",
      fallbackBg: "oklch(0.87 0.08 145)",
      fallbackText: "#1d6234",
    },
    dark: {
      bgVar: "--fulfillment-excellent-bg-dark",
      textVar: "--fulfillment-excellent-text-dark",
      fallbackBg: "oklch(0.58 0.13 155)",
      fallbackText: "oklch(0.88 0.05 155)",
    },
  },
  good: {
    light: {
      bgVar: "--fulfillment-good-bg-light",
      textVar: "--fulfillment-good-text-light",
      fallbackBg: "oklch(0.88 0.07 120)",
      fallbackText: "#2d5524",
    },
    dark: {
      bgVar: "--fulfillment-good-bg-dark",
      textVar: "--fulfillment-good-text-dark",
      fallbackBg: "oklch(0.56 0.12 120)",
      fallbackText: "oklch(0.87 0.05 120)",
    },
  },
  fair: {
    light: {
      bgVar: "--fulfillment-fair-bg-light",
      textVar: "--fulfillment-fair-text-light",
      fallbackBg: "oklch(0.90 0.08 75)",
      fallbackText: "#87591d",
    },
    dark: {
      bgVar: "--fulfillment-fair-bg-dark",
      textVar: "--fulfillment-fair-text-dark",
      fallbackBg: "oklch(0.54 0.14 85)",
      fallbackText: "oklch(0.88 0.06 85)",
    },
  },
  poor: {
    light: {
      bgVar: "--fulfillment-poor-bg-light",
      textVar: "--fulfillment-poor-text-light",
      fallbackBg: "oklch(0.90 0.07 30)",
      fallbackText: "#8a2f2f",
    },
    dark: {
      bgVar: "--fulfillment-poor-bg-dark",
      textVar: "--fulfillment-poor-text-dark",
      fallbackBg: "oklch(0.52 0.15 40)",
      fallbackText: "oklch(0.90 0.06 40)",
    },
  },
};

export function getFulfillmentPalette(tone: FulfillmentTone, mode: Mode) {
  const variant = mode === "dark" ? "dark" : "light";
  const entry = FULFILLMENT_PALETTE[tone][variant];
  return {
    background: getCssVar(entry.bgVar, entry.fallbackBg),
    text: getCssVar(entry.textVar, entry.fallbackText),
  };
}

// ------------------ Fulfillment Helpers ------------------ //

export const FULFILLMENT_STEPS = [
  { min: 1.0, tone: "excellent", emoji: "✅" },
  { min: 0.75, tone: "good", emoji: "🟢" },
  { min: 0.5, tone: "fair", emoji: "🟡" },
  { min: -Infinity, tone: "poor", emoji: "🔴" },
] as const;

export function resolveFulfillmentTone(value: number) {
  return FULFILLMENT_STEPS.find((s) => value >= s.min) || { tone: "poor", emoji: "🔴" };
}

export function getFulfillmentIndicator(value: number | null | undefined, mode: Mode) {
  if (value == null || Number.isNaN(value)) return null;
  const { tone, emoji } = resolveFulfillmentTone(value);
  const palette = getFulfillmentPalette(tone as FulfillmentTone, mode);
  const pct = `${parseFloat((value * 100).toFixed(1))}%`;
  return { emoji, color: palette.text, label: `${emoji} ${pct}`, pct };
}

export function colorFromFulfillment(fulfillment: number, mode: Mode): string {
  const { tone } = resolveFulfillmentTone(fulfillment);
  return getFulfillmentPalette(tone as FulfillmentTone, mode).background;
}
